<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToFloat',
	inheritAttrs: false
});
</script>
<template>
	<Teleport to="body" :disabled="!global">
		<div v-if="lazy ? ifShow : true" ref="el" class="to-float" :class="[setClass, attrs.class as string | string[] | undefined]" :style="[setStyle, attrs.style as Record<string, string | number> | undefined]" @mousedown="$emit('click')" @touchstart="$emit('click')" @mouseenter="$emit('mouseenter')" @mouseleave="$emit('mouseleave')" @click.stop>
			<div v-if="mask" ref="mask" class="to-float-mask" @click="maskClickHandle"></div>
			<div ref="wrapper" class="to-float-wrapper" :style="slide ? wrapperStyle : null">
				<div ref="content" class="to-float-content">
					<slot></slot>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick, onDeactivated, onActivated, CSSProperties, useAttrs } from 'vue'
import { floatProps, floatEmits } from './float'

// 获取当前 vue 实例


const props = defineProps(floatProps)
const emit = defineEmits(floatEmits)
const attrs = useAttrs()

const el = ref<HTMLElement | null>(null)
const origin = ref('top')
const ifShow = ref(false)
const scrollNode = ref<HTMLElement | null>(null)
const iposition = ref<string>(props.position || (props.slide ? 'right' : 'auto'))
const ialign = ref(props.align)
const link = ref<
	| HTMLElement
	| {
			x?: number
			touches?: TouchList
			clientX?: number
			clientY?: number
			offsetWidth?: number
			offsetHeight?: number
			getBoundingClientRect?: () => DOMRect
			target?: HTMLElement
			parentNode?: HTMLElement
	  }
	| null
>(null)
const isOpen = ref(false)
const isOver = ref(false)
const mask = ref<HTMLElement | null>(null)
const wrapper = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | null>(null)
const event = ref('')
const size = ref(1)
const contentObserver = ref<MutationObserver | null>(null)
let contentObserverTimer: ReturnType<typeof setTimeout> | null = null
let hasInitShow = false

watch(
	() => props.position,
	val => {
		iposition.value = val || (props.slide ? 'right' : 'auto')
	},
	{ immediate: true }
)
watch(
	() => props.align,
	val => {
		ialign.value = val
	},
	{ immediate: true }
)

const setClass = computed(() => {
	const arr = [] as string[]
	if (isOpen.value) arr.push('is-show')
	if (props.slide) {
		arr.push('is-slide')
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (iposition.value !== 'auto') arr.push(`position-${iposition.value}`)
	if (origin.value === 'bottom') arr.push('origin-bottom')
	if (props.mask) arr.push('is-mask')
	return arr
})
const transitionTransform = computed(() => `transform ${props.speed}ms ease-out`)

const setStyle = computed(() => {
	const obj: Record<string, any> = {}
	if (props.width && !props.slide) {
		if (!isNaN(Number(props.width))) obj.width = props.width + 'em'
		else if (props.width === 'full') obj.width = '100%'
		else obj.width = props.width
	}
	if (props.left) {
		if (isNaN(Number(props.left))) obj.left = props.left
		else obj.left = props.left + 'em'
	}
	if (props.top) {
		if (isNaN(Number(props.top))) obj.top = props.top
		else obj.top = props.top + 'em'
	}
	if (size.value !== 1) {
		obj.fontSize = size.value + 'em'
	}
	if (props.zIndex) {
		obj.zIndex = props.zIndex
	} else {
		obj.zIndex = (window as any).$TouchUI?.zIndex.toString()
		;(window as any).$TouchUI ? (window as any).$TouchUI.zIndex++ : ((window as any).$TouchUI = { zIndex: 1 })
	}
	return obj
})

const wrapperStyle = computed(() => {
	let obj: CSSProperties = {}

	// 宽度设置
	// 高度设置在打开的时候再设置，因为涉及到动态计算 wrapper 的高度
	if ((iposition.value === 'right' || iposition.value === 'left') && props.width) {
		if (!isNaN(Number(props.width))) obj.width = props.width + 'em'
		else if (props.width === 'full') obj.width = '100%'
		else obj.width = props.width
	}
	return obj
})

// 初始化
onMounted(() => {
	init()
})

onActivated(() => {
	init()
})

onBeforeUnmount(() => {
	destroy()
})

onDeactivated(() => {
	destroy()
})

function init() {
	if (!props.slide) {
		window.addEventListener('resize', close, false)
		return
	}
	if (props.show && !hasInitShow) {
		hasInitShow = true
		open()
	}
}

function destroy() {
	stopContentObserver()
	if (!props.slide) {
		document.body.removeEventListener('mousedown', blur)
		document.body.removeEventListener('touchstart', blur)
		window.removeEventListener('resize', close)
	}
	document.body.removeEventListener('scroll', close)
	document.body.removeEventListener('wheel', close)
}

function maskClickHandle() {
	if (props.closeOnBlur) {
		close()
	}
}

/** 记录 ToScroll 内容区滚动位置（slot 变化重算高度前调用） */
function captureScrollTops(root: HTMLElement | null) {
	const tops: { el: HTMLElement; top: number }[] = []
	if (!root) return tops
	root.querySelectorAll('.to-scroll-content').forEach(node => {
		const el = node as HTMLElement
		tops.push({ el, top: el.scrollTop })
	})
	return tops
}

function restoreScrollTops(tops: { el: HTMLElement; top: number }[]) {
	tops.forEach(({ el, top }) => {
		if (el.isConnected) el.scrollTop = top
	})
}

function set(link?: any, reposition = false) {
	// 重置 wrapper 高度，这样下次打开的时候才好重新计算是否超过整体高度
	if (props.slide) {
		setTimeout(() => {
			if ((iposition.value === 'top' || iposition.value === 'bottom') && !props.height && wrapper.value) {
				wrapper.value.style.height = ''
				wrapper.value.style.height = wrapper.value.getBoundingClientRect().height + 'px'
			}
		}, 0)
	} else if (link) {
		// reposition：仍要清空 wrapper 以按新内容测量高度；先记下内部滚动，避免 height='' 重排后滚回顶部
		const savedScrollTops = reposition ? captureScrollTops(content.value) : []
		if (!reposition) {
			if (wrapper.value) wrapper.value.style.height = ''
			if (el.value) {
				el.value.style.transition = 'none'
				el.value.style.transform = ''
			}
		} else {
			if (wrapper.value) wrapper.value.style.height = ''
		}
		nextTick(() => {
			// 宽度设置
			if (!props.width && el.value) {
				if ('x' in link || 'touches' in link) {
					el.value.style.width = '14em'
				} else {
					el.value.style.width = link.getBoundingClientRect().width + 'px'
				}
			}
			if (props.top === '' || props.left === '') {
				let x: number, y: number, linkWidth: number, linkHeight: number
				const wrapperWidth = wrapper.value?.getBoundingClientRect().width || 0
				const wrapperHeight = wrapper.value?.getBoundingClientRect().height || 0
				const bodyWidth = document.body.getBoundingClientRect().width
				const bodyHeight = document.body.getBoundingClientRect().height
				let topSpace: number, bottomSpace: number, leftSpace: number, rightSpace: number
				let useSpace = 0
				const dx = Number(props.dx) * 1 // 这里 em() 需根据实际情况补充
				const dy = Number(props.dy) * 1
				if ('clientX' in link || 'touches' in link) {
					x = 'clientX' in link ? link.clientX : link.touches[0].clientX
					y = 'clientY' in link ? link.clientY : link.touches[0].clientY
					linkWidth = 0
					linkHeight = 0
				} else {
					x = link.getBoundingClientRect().left
					y = link.getBoundingClientRect().top
					linkWidth = link.getBoundingClientRect().width
					linkHeight = link.getBoundingClientRect().height
				}
				topSpace = y
				bottomSpace = bodyHeight - y - linkHeight
				leftSpace = x
				rightSpace = bodyWidth - x - linkWidth
				// 计算相对位置
				if (!props.position) {
					if (bottomSpace >= wrapperHeight) {
						iposition.value = 'bottom'
					} else if (y >= wrapperHeight) {
						iposition.value = 'top'
					} else {
						if (y + linkHeight / 2 < bodyHeight / 2) {
							iposition.value = 'bottom'
						} else {
							iposition.value = 'top'
						}
					}
				}
				// 自动计算对齐方式
				if (props.align === 'auto') {
					if (iposition.value === 'top' || iposition.value === 'bottom') {
						if (rightSpace >= wrapperWidth) {
							ialign.value = 'left'
						} else {
							ialign.value = 'right'
						}
					} else {
						if (y + linkHeight / 2 < bodyHeight / 2) {
							ialign.value = 'top'
						} else {
							ialign.value = 'bottom'
						}
					}
				}
				if (el.value) {
					if (iposition.value === 'top') {
						el.value.style.top = ''
						el.value.style.bottom = bottomSpace + linkHeight - 1 - dy + 'px'
					}
					if (iposition.value === 'bottom') {
						el.value.style.top = topSpace + linkHeight - 1 + dy + 'px'
						el.value.style.bottom = ''
					}
					if (iposition.value === 'top' || iposition.value === 'bottom') {
						if (ialign.value === 'left') {
							el.value.style.left = leftSpace + dx + 'px'
							el.value.style.right = ''
						} else if (ialign.value === 'center') {
							el.value.style.left = leftSpace - wrapperWidth / 2 + linkWidth / 2 + 'px'
							el.value.style.right = ''
						} else {
							el.value.style.right = rightSpace - dx + 'px'
							el.value.style.left = ''
						}
					}
					if (iposition.value === 'left') {
						el.value.style.right = rightSpace + linkWidth - dx - 1 + 'px'
						el.value.style.left = ''
					}
					if (iposition.value === 'right') {
						el.value.style.left = leftSpace + linkWidth + dx - 1 + 'px'
						el.value.style.right = ''
					}
					if (iposition.value === 'left' || iposition.value === 'right') {
						if (ialign.value === 'bottom') {
							el.value.style.top = ''
							el.value.style.bottom = bottomSpace - dy + 'px'
						} else {
							el.value.style.top = topSpace + dy + 'px'
							el.value.style.bottom = ''
						}
					}
					if (iposition.value === 'bottom') {
						useSpace = bottomSpace
						origin.value = 'top'
					} else if (iposition.value === 'top') {
						useSpace = topSpace
						origin.value = 'bottom'
					} else {
						if (ialign.value === 'bottom') {
							useSpace = topSpace + linkHeight
							origin.value = 'bottom'
						} else if (ialign.value === 'top') {
							useSpace = bottomSpace + linkHeight
							origin.value = 'top'
						} else {
							console.error('[ToFloat] 浮层对齐方式设置错误！position 在左右两侧时，对齐方式应是顶部或底部')
						}
					}
					if (useSpace < wrapperHeight && wrapper.value) {
						isOver.value = true
						wrapper.value.style.height = useSpace + 'px'
					}
					// 自动计算大小
					if (link) {
						const ratio = link.getBoundingClientRect().width / link.offsetWidth
						const ratio2 = Number(getComputedStyle(link).fontSize.trim().toLowerCase().replace(/px$/, '')) / Number(getComputedStyle(document.body).fontSize.trim().toLowerCase().replace(/px$/, ''))
						size.value = ratio * ratio2
					}
				}
			}
			// 打开：播放入场动画并绑定滚动关闭；仅调整布局：浮层已展开，不再走 setOpenStyle，避免等同「再打开」
			if (el.value && !reposition) {
				setOpenStyle()
			}
			if (link && !('x' in link) && !('touches' in link) && !reposition) {
				setScrollStatus()
			}
			// 内容变化后高度已更新，把内部列表滚动恢复到重算前
			if (reposition && savedScrollTops.length) {
				nextTick(() => {
					restoreScrollTops(savedScrollTops)
					requestAnimationFrame(() => restoreScrollTops(savedScrollTops))
				})
			}
		})
	}
}

function open(by?: any) {
	if (props.beforeOpen) {
		const fn = props.beforeOpen()
		if (fn && typeof (fn as any).then === 'function') {
			;(fn as Promise<boolean>)
				.then(() => {
					openAction(by)
				})
				.catch(error => {
					console.log(error)
				})
		} else {
			if (fn) openAction(by)
		}
	} else {
		openAction(by)
	}
}

function openAction(by?: any) {
	if (!isOpen.value) {
		emit('beforeOpen')

		if (props.slide) {
			// 打开
			if (props.lazy) {
				ifShow.value = true
			}

			// 设置样式
			nextTick(() => {
				set()
				setOpenStyle()
				startContentObserver()
			})

			// 后置事件
			if (props.afterOpen && Object.prototype.toString.call(props.afterOpen) === '[object Function]') {
				props.afterOpen()
			}

			// 后置动作
			let timer: any = null
			timer = setTimeout(() => {
				emit('opened')
				clearTimeout(timer)
				timer = null
			}, Number(props.speed))

			isOpen.value = true
		} else {
			document.body.addEventListener('mousedown', blur)
			document.body.addEventListener('touchstart', blur)

			if (by) {
				if (by.clientX || (by.touches && by.touches[0].clientX)) {
					link.value = by
					if (by.clientX || (by.touches && by.touches[0].clientX)) {
						event.value = 'contextmenu'
					}
					if (by.type === 'contextmenu') {
						by.preventDefault()
					}
				} else {
					// 兼容处理：如果by是DOM元素则直接使用，否则通过$refs.el获取
					if (by instanceof HTMLElement) {
						link.value = by
					} else {
						getElementFromRef(by).then(element => {
							link.value = element
						})
					}
				}
			}

			// 打开
			if (props.lazy) {
				ifShow.value = true
			}

			// 设置其他依赖项
			setTimeout(() => {
				set(link.value)
				startContentObserver()
			}, 0)

			// 后置事件
			if (props.afterOpen && Object.prototype.toString.call(props.afterOpen) === '[object Function]') {
				props.afterOpen()
			}

			// 后置动作
			emit('opened')
			isOpen.value = true
		}
	}
}

function setOpenStyle() {
	if (props.slide) {
		if (iposition.value === 'top') {
			wrapper.value.style.transform = 'translateY(-100%)'
		}
		if (iposition.value === 'bottom') {
			wrapper.value.style.transform = 'translateY(100%)'
		}
		if (iposition.value === 'right') {
			wrapper.value.style.transform = 'translateX(100%)'
		}
		if (iposition.value === 'left') {
			wrapper.value.style.transform = 'translateX(-100%)'
		}
		setTimeout(() => {
			wrapper.value.style.transition = transitionTransform.value
			wrapper.value.style.transform = 'translateY(0)'
			setTimeout(() => {
				wrapper.value.style.transform = ''
			}, Number(props.speed))
		}, 0)
	} else {
		// 先固定为收起态且无过渡，再下一帧加 transition 并展开，避免与布局测量同一帧合并导致无动画
		el.value.style.transition = 'none'
		el.value.style.transform = 'scale(1, 0)'
		el.value.style.visibility = 'visible'
		requestAnimationFrame(() => {
			el.value.style.transition = transitionTransform.value
			el.value.style.transform = 'scale(1)'
			setTimeout(() => {
				el.value.style.transform = ''
				el.value.style.transition = ''
			}, Number(props.speed))
		})
	}
	el.value.style.zIndex = (window as any).$TouchUI?.zIndex
}

function close(e?: Event) {
	// float 组件内部滚动不关闭
	if (e && el.value && e.target && typeof Node !== 'undefined' && e.target instanceof Node && el.value.contains(e.target)) {
		return
	}
	if (isOpen.value) {
		if (props.beforeClose) {
			const fn = props.beforeClose()
			if (fn && typeof (fn as any).then === 'function') {
				;(fn as Promise<boolean>)
					.then(() => {
						closeAction()
					})
					.catch(error => {
						console.log(error)
					})
			} else {
				if (fn) closeAction()
			}
		} else {
			closeAction()
		}
	}
}

function closeAction() {
	stopContentObserver()
	// 参数重置
	if (!props.height && wrapper.value) {
		wrapper.value.style.height = ''
	}
	isOver.value = false

	if (props.slide) {
		setCloseStyle()
		isOpen.value = false
	} else {
		// 移除事件
		if (scrollNode.value) {
			scrollNode.value.removeEventListener('scroll', close)
		}

		// 前置事件
		emit('beforeClose')

		// 样式设置
		setCloseStyle()

		// 关闭
		isOpen.value = false
	}

	setTimeout(() => {
		// 销毁
		if (props.lazy) {
			ifShow.value = false
		}

		// 后置事件
		emit('closed')

		// 后置方法
		if (props.afterClose && Object.prototype.toString.call(props.afterClose) === '[object Function]') {
			props.afterClose()
		}
	}, Number(props.speed))
}

function setCloseStyle() {
	if (props.slide) {
		if (iposition.value === 'top') {
			wrapper.value.style.transform = 'translateY(-100%)'
		}
		if (iposition.value === 'bottom') {
			wrapper.value.style.transform = 'translateY(100%)'
		}
		if (iposition.value === 'right') {
			wrapper.value.style.transform = 'translateX(100%)'
		}
		if (iposition.value === 'left') {
			wrapper.value.style.transform = 'translateX(-100%)'
		}
	} else {
		// 打开结束时会清空 transition，这里必须带上 transition，否则收起没有过渡
		el.value.style.transition = transitionTransform.value
		el.value.style.transform = 'scale(1, 0)'
	}
}

function toggle(by?: any) {
	if (isOpen.value) {
		close()
	} else {
		if (by) {
			open(by)
		} else {
			open()
		}
	}
}

function blur(e: MouseEvent | TouchEvent) {
	if (!props.closeOnBlur) return
	// 同步阶段：检查点击目标是否在之后打开的浮层内（DOM 顺序更靠后）
	// 所有浮层都 teleport 到 body，DOM 顺序即打开顺序
	if (el.value && e.target instanceof Node) {
		const allFloats = Array.from(document.querySelectorAll('.to-float.is-show'))
		const currentIdx = allFloats.indexOf(el.value)
		for (let i = currentIdx + 1; i < allFloats.length; i++) {
			if (allFloats[i].contains(e.target)) {
				return
			}
		}
	}
	nextTick(() => {
		let node = e.target as HTMLElement | null
		if (event.value === 'contextmenu') {
			// 右键点击，任意位置均隐藏 && node !== this.$el
			while (node && node.nodeName !== 'BODY') {
				node = node.parentNode as HTMLElement | null
			}
		} else if (link.value && 'target' in link.value && link.value.target) {
			while (node && node !== el.value && node !== link.value.target && node.nodeName !== 'BODY') {
				node = node.parentNode as HTMLElement | null
			}
		} else {
			while (node && node !== el.value && node !== link.value && node.nodeName !== 'BODY') {
				node = node.parentNode as HTMLElement | null
			}
		}
		if (node && node.nodeName === 'BODY') {
			close()
		}
	})
}

function setScrollStatus() {
	document.body.addEventListener('scroll', close, { passive: true })
	document.body.addEventListener('wheel', close, { passive: true })
}

async function getElementFromRef(refOrNode) {
	await nextTick() // 保证 DOM 已渲染
	if (!refOrNode) return null
	const val = refOrNode.value ?? refOrNode
	if (!val) return null
	if (val instanceof Element || val instanceof Text) return val
	if (val.$el) return val.$el
	if (val._vnode && val._vnode.el) return val._vnode.el
	return null
}

function startContentObserver() {
	stopContentObserver()
	if (content.value) {
		contentObserver.value = new MutationObserver(() => {
			if (isOpen.value) {
				if (contentObserverTimer) clearTimeout(contentObserverTimer)
				contentObserverTimer = setTimeout(() => {
					set(link.value, true)
				}, 0)
			}
		})
		contentObserver.value.observe(content.value, {
			childList: true,
			subtree: true,
			characterData: true,
			attributes: true,
			attributeFilter: ['style', 'class']
		})
	}
}

function stopContentObserver() {
	if (contentObserver.value) {
		contentObserver.value.disconnect()
		contentObserver.value = null
	}
	if (contentObserverTimer) {
		clearTimeout(contentObserverTimer)
		contentObserverTimer = null
	}
}

// 暴露方法
defineExpose({ open, close, toggle, set })
</script>
